import DetailsTitle from "components/common/detailsPage/DetailsTitle";
import DetailsShimmer from "components/common/shimmer/DetailsShimmer";
import { ApiLogBadge } from "components/common/StatusBadge";
import { usePermission } from "hooks/usePermission";
import { useQueryParam } from "hooks/useQueryParam";
import React, { useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { formatUTCDate } from "utils/utils";
import InfoItem from "components/common/detailsPage/InfoItem";
import { getAPILogDetails } from "store/slices/apiLogsSlice";
import { Copy, Download } from "lucide-react";
import { showAlert } from "store/slices/alertSlice";
import HorizontalScroller from "components/common/HorizontalScroller";

const APILogDetails = () => {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const dispatch = useDispatch();
  const id = useQueryParam("id");

  const { canAction } = usePermission();
  const { canView } = canAction("api_logs");
  const [activeTab, setActiveTab] = useState(1);

  const { loading, itemDetails } = useSelector((state) => state.APILogs);

  const { userId, data, createdAt } = itemDetails || {};

  useEffect(() => {
    if (id && canView) {
      dispatch(getAPILogDetails(id));
    }
  }, [id, canView]);

  const formattedData = (data) => data?.replace(/\n/g, "<br>");

  const copyToClipboard = (data, type) => {
    const dataString = JSON.stringify(data, null, 2);

    const textarea = document.createElement("textarea");
    textarea.value = dataString;
    textarea.setAttribute("readonly", "");
    textarea.style.position = "absolute";
    textarea.style.left = "-9999px";
    document.body.appendChild(textarea);

    try {
      textarea.select();
      const successful = document.execCommand("copy");
      if (successful) {
        dispatch(
          showAlert({
            text: t(`${type} copied to clipboard!`),
            type: "success",
          }),
        );
      } else {
        dispatch(showAlert({ text: t("Failed to copy"), type: "failed" }));
      }
    } catch (err) {
      dispatch(showAlert({ text: t("Failed to copy"), type: "failed" }));
    } finally {
      document.body.removeChild(textarea);
    }
  };

  const downloadJSON = (data) => {
    const dataString = JSON.stringify(data, null, 2);
    const blob = new Blob([dataString], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "data.json";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const infoFields = [
    // ───── Customer Info ─────
    {
      label: t("User Id"),
      render: (
        <HorizontalScroller t={t}>
          <div className="value">{userId?._id || "-"}</div>
        </HorizontalScroller>
      ),
      col: { lg: 4 },
    },
    {
      label: t("Customer Name"),
      value: userId?.name?.trim() || "-",
      col: { lg: 4 },
    },
    {
      label: t("Customer Phone Number"),
      value: userId?.phone || "-",
      col: { lg: 4 },
    },

    { isSpacer: true },

    // ───── Request Info ─────
    {
      label: t("Date & Time"),
      value: formatUTCDate(createdAt) || "-",
      col: { lg: 4 },
    },
    {
      label: t("Duration"),
      value: data?.duration || "-",
      col: { lg: 4 },
    },
    {
      label: t("IP Address"),
      value: data?.ipAddress || "-",
      col: { lg: 4 },
    },

    { isSpacer: true },

    // ───── API Info ─────
    {
      label: t("Method"),
      render: <ApiLogBadge type="method" status={data?.method} t={t} />,
      col: { lg: 4 },
    },
    {
      label: t("URL"),
      value: data?.url || "-",
      render: (
        <HorizontalScroller t={t}>
          <div className="value">{data?.url}</div>
        </HorizontalScroller>
      ),

      col: { lg: 4 },
    },

    {
      label: t("Memory Used"),
      value: data?.memoryUsed || "-",
      col: { lg: 4 },
    },

    { isSpacer: true },

    // ───── Response Info ─────
    {
      label: t("Status Code"),
      render: <ApiLogBadge type="result" status={data?.responseStatus} t={t} />,
      col: { lg: 4 },
    },
    {
      label: t("Status"),
      render: (
        <ApiLogBadge
          type="result"
          extra={data?.status}
          status={data?.status}
          t={t}
        />
      ),
      col: { lg: 4 },
    },
  ];

  const deviceinfoFields = [
    {
      label: t("App Version"),
      value: userId?.deviceInfo?.appVersion || "-",
      col: { lg: 4 },
    },
    {
      label: t("Device Id"),
      render: (
        <HorizontalScroller t={t}>
          <div className="value">{userId?.deviceInfo?.deviceId || "-"}</div>
        </HorizontalScroller>
      ),

      col: { lg: 4 },
    },
    {
      label: t("FCM Token"),
      render: (
        <HorizontalScroller t={t}>
          <div className="value">{userId?.deviceInfo?.fcmToken || "-"}</div>
        </HorizontalScroller>
      ),
      col: { lg: 4 },
    },
    { isSpacer: true },
    {
      label: t("Model"),
      value: userId?.deviceInfo?.model || "-",
      col: { lg: 4 },
    },
    {
      label: t("OS Version"),
      value: userId?.deviceInfo?.osVersion || "-",
      col: { lg: 4 },
    },
    {
      label: t("Platform"),
      value: userId?.deviceInfo?.platform || "-",
      col: { lg: 4 },
    },
  ];

  const shimmerLayout = [
    {
      col: 6,
      blocks: [
        { rows: 3, cols: 3 },
        { rows: 1, cols: 2 },
      ],
    },
    {
      col: 6,
      blocks: [{ rows: 2, cols: 3 }],
    },
    {
      col: 12,
      blocks: [{ rows: 1, cols: 3 }],
    },
    {
      col: 12,
      type: "details_page_tab",
      blocks: [{ rows: 1, cols: 1 }],
    },
  ];

  const TABS = [
    { id: 1, label: "Request", dataKey: "requests" },
    { id: 2, label: "Headers", dataKey: "headers" },
    { id: 3, label: "Response", dataKey: "response" },
  ];

  const renderTabContent = () => {
    const currentTab = TABS.find((tab) => tab.id === activeTab);
    if (!currentTab) return null;

    const tabData = data?.[currentTab.dataKey];

    return (
      <div
        className="bg-black text-white p-3 rounded-4 position-relative"
        style={{ maxHeight: 600, overflowY: "auto" }}
      >
        <pre
          dangerouslySetInnerHTML={{
            __html: formattedData(JSON.stringify(tabData, null, 2)),
          }}
        />
        <Copy
          style={{
            position: "absolute",
            top: 20,
            right: 55,
            cursor: "pointer",
          }}
          onClick={() =>
            copyToClipboard(tabData, t(currentTab.label.toLowerCase()))
          }
        />
        <Download
          style={{
            position: "absolute",
            top: 20,
            right: 20,
            cursor: "pointer",
          }}
          onClick={() => downloadJSON(tabData)}
        />
      </div>
    );
  };

  if (!canView) return null;

  if (loading || !itemDetails) {
    return (
      <div className="page-content">
        <DetailsTitle title={t("API Log Details")} />
        <DetailsShimmer layout={shimmerLayout} />
      </div>
    );
  }

  return (
    <div className="page-content common_details_page api_log_details">
      <DetailsTitle title={t("API Log Details")} />

      <Row>
        <Col lg={6} className="">
          <div className="common_card" style={{ height: "calc(100%  - 20px)" }}>
            <Row id="general_detail_row">
              <Col lg={12} className="mb-3 details_heading">
                <h4>{t("Customer Details")}</h4>
              </Col>

              {infoFields.map((item, index) => {
                if (item.isSpacer) {
                  return <div key={index} className="mt-4 w-100" />;
                }

                return (
                  <InfoItem
                    key={index}
                    label={item.label}
                    value={item.value}
                    col={item.col}
                  >
                    {item.render}
                  </InfoItem>
                );
              })}
            </Row>
          </div>
        </Col>
        <Col lg={6} className="">
          <div className="common_card" style={{ height: "calc(100%  - 20px)" }}>
            <Row id="device_detail_row">
              <Col lg={12} className="mb-3 details_heading">
                <h4>{t("Customer Device Information")}</h4>
              </Col>
              {deviceinfoFields.map((item, index) => {
                if (item.isSpacer) {
                  return <div key={index} className="mt-4 w-100" />;
                }

                return (
                  <InfoItem
                    key={index}
                    label={item.label}
                    value={item.value}
                    col={item.col}
                  >
                    {item.render}
                  </InfoItem>
                );
              })}
            </Row>
          </div>
        </Col>
      </Row>

      {/* Custom Tabs Section */}
      <div className="common_tabs mt_20">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            className={`tabs_btn ${activeTab === tab.id ? "active" : ""}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {t(tab.label)}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <Row className="mt-2">
        <Col lg={12}>
          <div className="common_card">{renderTabContent()}</div>
        </Col>
      </Row>
    </div>
  );
};

export default APILogDetails;
